import { useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { apiApplicationFileValidatorLog } from 'api';
import { DeferredDocument, ValidationInfo } from 'types/documents';

interface UseDocumentValidationProps {
  documents: DeferredDocument[];
  policyUuid: string;
}

export const useDocumentValidation = ({
  documents,
  policyUuid,
}: UseDocumentValidationProps) => {
  const [validatingDocs, setValidatingDocs] = useState<{
    [docKey: string]: ValidationInfo;
  }>({});

  const toast = useToast();
  const queryClient = useQueryClient();

  // Initialize validation state from backend data on mount and when documents change
  useEffect(() => {
    const initialValidations: typeof validatingDocs = {};

    documents.forEach((doc) => {
      if (doc.validation_log) {
        const docKey = `${doc.validation_key}-${doc.item_key}`;
        initialValidations[docKey] = {
          validationLogId: doc.validation_log.id,
          status: doc.validation_log.status,
          result: doc.validation_log.result,
        };
      }
    });

    // Update state with new validation logs, preserving existing ones
    setValidatingDocs((prev) => {
      const updated = { ...prev };

      // Add new validation logs from backend
      Object.entries(initialValidations).forEach(([docKey, validation]) => {
        // Only update if not already tracking, or if backend status is more recent
        if (
          !updated[docKey] ||
          updated[docKey].validationLogId !== validation.validationLogId
        ) {
          updated[docKey] = validation;
        }
      });

      return updated;
    });
  }, [documents]);

  // Poll validation status for documents being validated
  useEffect(() => {
    const docsToValidate = Object.entries(validatingDocs).filter(
      ([_, info]) => info.status === 'created' || info.status === 'validating',
    );

    if (docsToValidate.length === 0) return;

    const pollValidation = async () => {
      for (const [docKey, info] of docsToValidate) {
        try {
          const response = await apiApplicationFileValidatorLog(
            policyUuid,
            info.validationLogId,
          );

          // Update validation status
          setValidatingDocs((prev) => ({
            ...prev,
            [docKey]: {
              ...prev[docKey],
              status: response.status,
              result: response.result,
            },
          }));

          // If validation completed (approved or failed), show notification
          if (response.status === 'approved') {
            toast({
              title: 'File validated successfully',
              description: 'The uploaded document passed validation',
              status: 'success',
              duration: 3000,
              isClosable: true,
              position: 'top',
            });

            // Refetch documents to update UI
            queryClient.invalidateQueries({ queryKey: ['deferredDocuments'] });
          } else if (response.status === 'failed') {
            toast({
              title: 'Validation failed',
              description:
                response.result || 'The document did not pass validation',
              status: 'warning',
              duration: 5000,
              isClosable: true,
              position: 'top',
            });
          }
        } catch (error) {
          console.error('Failed to poll validation status:', error);
        }
      }
    };

    // Poll immediately and then every 2 seconds
    pollValidation();
    const interval = setInterval(pollValidation, 2000);

    return () => clearInterval(interval);
  }, [validatingDocs, policyUuid, toast, queryClient]);

  const startValidation = (docKey: string, validationLogId: number) => {
    setValidatingDocs((prev) => ({
      ...prev,
      [docKey]: {
        validationLogId,
        status: 'created',
      },
    }));
  };

  const clearValidation = (docKey: string) => {
    setValidatingDocs((prev) => {
      const updated = { ...prev };
      delete updated[docKey];
      return updated;
    });
  };

  return {
    validatingDocs,
    startValidation,
    clearValidation,
  };
};
